import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useTour } from '../components/tour/TourProvider';
import { canOfferTour, loadTourProgress, saveTourProgress } from '../services/tourProgress';
import { WELCOME_TOUR_ID } from '../tours/definitions/welcomeTour';
import { loadTour } from '../tours/loadTour';
import { isTokenValid } from '../utils';
import { WINDOW_MEDIA } from '../config/windowClasses';
import { getSessionUserId, useOnSessionChange } from '../utils/session';

/**
 * THE WELCOME TOUR'S CALLER (KPR-613). The tour engine saves nothing and the
 * progress service fetches only when asked; this is the one place that joins
 * them for the welcome tour:
 *
 *   - It reads the person's tour progress ONCE, as soon as their session
 *     starts (`loadTourProgress`, KPR-612), so the answer is in before Home
 *     draws and its offer never flickers in afterwards. A read that fails, or
 *     does not answer in a few seconds, is "we do not know": the offer stays
 *     hidden (`canOfferTour`) and the account menu still starts the tour.
 *   - It starts the tour (`start`) for Home's card and for the account menu's
 *     "Take the tour", the same way whatever the saved state is, and saves what
 *     the person chose: Finish is `completed`; Exit tour, Esc and "Not now" are
 *     `dismissed`. The engine's third ending, `stopped` (the app ended it: a
 *     route change, an unmount, a replacing tour), is NOT the person's choice
 *     and saves nothing.
 *   - It holds the sidebar open for the length of the tour (`railForcedOpen`;
 *     Layout reads it). The person's own collapsed choice is never rewritten,
 *     so ending the tour, however it ends, puts the sidebar back as it was.
 *
 * Per account, like every cache (utils/session.js): another account signing in
 * on the same tab starts from nothing and never sees the last one's progress.
 * Signed out, nothing is requested. Like PreferencesContext it lives at the
 * root and follows the session, not a route.
 *
 * THE SHOWCASE CARD (KPR-1930) rides on the same read: its Not now is a
 * record of its own in the same store, `showcase-card` (the store is open by
 * id, keepr-api SCHEMA.md § Product-tour progress), so it is remembered on the
 * account and the card does not return on any device. `showcaseCard.offered`
 * is true only when the read worked and holds no such record; who the card is
 * for (a new, ordinary account) is components/home/ShowcaseOffer.js's call.
 *
 * `useWelcomeTour()` is null-tolerant: outside a provider (a test, a public
 * page) nothing is offered and `start` does nothing.
 */
const WelcomeTourContext = createContext(null);

// A progress read that has not answered by then is treated as failed, so the
// page waiting on it (Home) is never held by it for longer than this.
export const PROGRESS_READ_TIMEOUT_MS = 4000;

// The Showcase card's record in the tour-progress store (KPR-1930). Version 1:
// a later version of the card would offer it again to people who said no.
export const SHOWCASE_CARD_ID = 'showcase-card';
export const SHOWCASE_CARD_VERSION = 1;

const INERT = Object.freeze({
	loading: false,
	offered: false,
	running: false,
	railForcedOpen: false,
	start: () => false,
	dismiss: () => {},
	showcaseCard: Object.freeze({ offered: false, dismiss: () => {} }),
});

const devLog = (message, error) => {
	if (process.env.NODE_ENV === 'development') console.info(`[tour] ${message}`, error || '');
};

const isCompactScreen = () => (
	typeof window !== 'undefined'
	&& typeof window.matchMedia === 'function'
	// compact, or short (§18): the shell draws the tab bar, not the sidebar.
	&& Boolean(window.matchMedia(WINDOW_MEDIA.compact).matches || window.matchMedia(WINDOW_MEDIA.short).matches)
);

export function WelcomeTourProvider({ children }) {
	const tourApi = useTour();
	const [progress, setProgress] = useState(null);
	const [loading, setLoading] = useState(() => isTokenValid());
	const [railForcedOpen, setRailForcedOpen] = useState(false);
	const requestedRef = useRef(false);
	const progressRef = useRef(null);
	// What this session has saved itself; laid over a read that answers later,
	// so a tour taken before the answer is never forgotten by it.
	const savedHereRef = useRef({});
	const runRef = useRef(0);
	const tourApiRef = useRef(tourApi);
	tourApiRef.current = tourApi;

	const commit = useCallback((next) => {
		progressRef.current = next;
		setProgress(next);
	}, []);

	// Another account (or none) on this tab: nothing carries over, and the
	// latch re-arms for the next sign-in.
	const session = useOnSessionChange(() => {
		requestedRef.current = false;
		savedHereRef.current = {};
		runRef.current += 1;
		progressRef.current = null;
		setProgress(null);
		setRailForcedOpen(false);
		setLoading(isTokenValid());
	});

	const load = useCallback(async () => {
		if (requestedRef.current) return;
		if (!isTokenValid()) {
			setLoading(false);
			return;
		}
		requestedRef.current = true;
		const startedFor = getSessionUserId();
		setLoading(true);
		let timer = null;
		const answer = await Promise.race([
			loadTourProgress(),
			new Promise((resolve) => {
				timer = setTimeout(() => resolve({ ok: false, tours: {} }), PROGRESS_READ_TIMEOUT_MS);
			}),
		]);
		clearTimeout(timer);
		if (getSessionUserId() !== startedFor) return; // answered for the last account
		commit({ ok: Boolean(answer && answer.ok), tours: { ...((answer && answer.tours) || {}), ...savedHereRef.current } });
		setLoading(false);
	}, [commit]);

	useEffect(() => {
		load();
	}, [load, session]);

	const version = useMemo(() => {
		const tour = loadTour(WELCOME_TOUR_ID);
		return tour ? tour.version : null;
	}, []);

	// Saves one ending at the version the person saw. Optimistic: the card goes
	// at once, and a save that fails is not the person's business (the next
	// sign-in reads the server and may offer it again).
	const save = useCallback((tourId, status, seenVersion) => {
		const current = progressRef.current;
		const entry = { status, version: seenVersion, updatedAt: new Date().toISOString() };
		savedHereRef.current = { ...savedHereRef.current, [tourId]: entry };
		commit({ ok: Boolean(current && current.ok), tours: { ...((current && current.tours) || {}), [tourId]: entry } });
		saveTourProgress(tourId, status, seenVersion).catch((error) => devLog(`saving "${tourId}" as ${status} failed`, error));
	}, [commit]);

	const record = useCallback((status, seenVersion) => {
		const current = progressRef.current;
		const existing = current && current.tours ? current.tours[WELCOME_TOUR_ID] : null;
		// Leaving a replay is not "dismissing" a tour already finished.
		if (status === 'dismissed' && existing && existing.status === 'completed' && existing.version >= seenVersion) return;
		save(WELCOME_TOUR_ID, status, seenVersion);
	}, [save]);

	const start = useCallback(() => {
		if (version === null) return false;
		const api = tourApiRef.current;
		// A second press while it runs is the same press.
		if (api.active && api.active.id === WELCOME_TOUR_ID) return true;
		runRef.current += 1;
		const token = runRef.current;
		setRailForcedOpen(true);
		const began = api.start(WELCOME_TOUR_ID, {
			context: { compact: isCompactScreen() },
			onComplete: (result) => record('completed', result.version),
			onDismiss: (result) => record('dismissed', result.version),
			// A tour this one replaced ends later than this one began: only
			// the latest run may put the sidebar back.
			onExit: () => { if (runRef.current === token) setRailForcedOpen(false); },
		});
		if (!began) setRailForcedOpen(false);
		return began;
	}, [record, version]);

	const dismiss = useCallback(() => {
		if (version !== null) record('dismissed', version);
	}, [record, version]);

	const offered = !loading && version !== null && canOfferTour(progress, WELCOME_TOUR_ID, version);

	const dismissShowcase = useCallback(() => save(SHOWCASE_CARD_ID, 'dismissed', SHOWCASE_CARD_VERSION), [save]);
	const showcaseOffered = !loading && canOfferTour(progress, SHOWCASE_CARD_ID, SHOWCASE_CARD_VERSION);
	const showcaseCard = useMemo(() => ({ offered: showcaseOffered, dismiss: dismissShowcase }), [showcaseOffered, dismissShowcase]);

	const value = useMemo(() => ({
		loading,
		offered,
		running: Boolean(tourApi.active && tourApi.active.id === WELCOME_TOUR_ID),
		railForcedOpen,
		start,
		dismiss,
		showcaseCard,
	}), [loading, offered, tourApi.active, railForcedOpen, start, dismiss, showcaseCard]);

	return (
		<WelcomeTourContext.Provider value={value}>
			{children}
		</WelcomeTourContext.Provider>
	);
}

/** `{ loading, offered, running, railForcedOpen, start(), dismiss(), showcaseCard: { offered, dismiss() } }`; inert without a provider. */
export function useWelcomeTour() {
	return useContext(WelcomeTourContext) || INERT;
}
